import { useMemo } from "react";

const NO_OFFERS = {};

/**
 * Packs on sale that would lower the per-session price of a bookable session.
 *
 * Which packs qualify is decided by the server, against the same rules that
 * redeem one at checkout — session length read through the tier it was sold
 * against, the pack's plans covering this category and service, and the
 * location it was actually sold at. Deciding it here from the catalogue is what
 * let the hint advertise packs that could never be spent on the session.
 *
 * The answers travel with the sessions (`packageOffers` on the timetable and
 * detail payloads, keyed by each session's `offerKey`), so a card is drawn with
 * its hint already on it. Asking in a call of our own is what made the hint pop
 * in a moment after the cards, and again on every filter change.
 */
export default function usePackageOffers(packageOffers) {
  const offers = packageOffers || NO_OFFERS;

  // The best saving available on one session, or null when the client already
  // holds a pack for it, none covers it, or none actually beats the price on the
  // card. Savings are measured against that displayed price so the two numbers
  // always agree. Among packs that all qualify, the one bought against this
  // session's own pricing tier wins, then the deepest saving.
  return useMemo(
    () => (session) => {
      if (!session?.offerKey || session.price == null) return null;
      const entry = offers[session.offerKey];
      if (!entry || entry.alreadyCovered) return null;

      let best = null;
      for (const variant of entry.variants || []) {
        if (variant.pricePerSession == null) continue;
        const saving = session.price - variant.pricePerSession;
        if (saving <= 0) continue;

        const exactPricing = session.pricingId != null && variant.pricingId === session.pricingId;
        const candidate = { ...variant, saving, exactPricing };
        if (!best || (candidate.exactPricing && !best.exactPricing) || (candidate.exactPricing === best.exactPricing && saving > best.saving)) {
          best = candidate;
        }
      }
      return best;
    },
    [offers]
  );
}
